import { useEffect, useRef, useState } from 'react';
import { Navigation, Loader2, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import maplibregl from 'maplibre-gl';

import noPropertiesPng from '@/assets/illustrations/empty/no-properties.png';
import noMapLocationsPng from '@/assets/illustrations/empty/no-map-locations.png';

import { usePropertyStore } from '@/stores/property-store';
import { useWaypointStore } from '@/stores/waypoint-store';
import { PropertyMap } from '@/components/map/PropertyMap';
import { PropertyPill } from '@/components/map/PropertyPill';
import { WindInfoBadge } from '@/components/map/WindInfoBadge';
import { OperationalGuide } from '@/components/common/OperationalGuide';
import { TypePickerSheet } from '@/components/waypoint/TypePickerSheet';
import type { WaypointType } from '@/types/waypoint';

export default function MapPage() {
  const navigate = useNavigate();
  const { properties, isLoading: propertiesLoading, loadProperties } = usePropertyStore();
  const { waypoints, loadWaypoints } = useWaypointStore();
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');
  const [isGpsLocating, setIsGpsLocating] = useState(false);
  const [typePickerOpen, setTypePickerOpen] = useState(false);
  const mapRef = useRef<maplibregl.Map | null>(null);

  useEffect(() => {
    if (properties.length === 0) {
      void loadProperties();
    }
  }, [properties.length, loadProperties]);

  const effectivePropertyId = selectedPropertyId || (properties[0]?.id ?? '');
  const selectedProperty = properties.find((p) => p.id === effectivePropertyId);

  useEffect(() => {
    if (effectivePropertyId) {
      void loadWaypoints(effectivePropertyId);
    }
  }, [effectivePropertyId, loadWaypoints]);

  function handleTypeSelected(type: WaypointType) {
    setTypePickerOpen(false);
    void navigate(`/land/properties/${effectivePropertyId}/waypoints/new?type=${type}`);
  }

  function handleGps() {
    if (!navigator.geolocation || !mapRef.current) return;
    setIsGpsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        mapRef.current?.flyTo({
          center: [pos.coords.longitude, pos.coords.latitude],
          zoom: 15,
          duration: 800,
        });
        setIsGpsLocating(false);
      },
      () => { setIsGpsLocating(false); },
      { timeout: 10000, enableHighAccuracy: true },
    );
  }

  if (propertiesLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-bark-900">
        <p className="text-body-sm text-bark-400">Loading map…</p>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-bark-900" data-testid="map-no-properties">
        <OperationalGuide
          illustration={noPropertiesPng}
          heading="Pin Your First Waypoint"
          subtext="Mark stands, plots, and trails to map your land."
          ctaLabel="Add your first property"
          ctaLink="/land/properties/new"
          data-guide-id="map-no-properties"
        />
      </div>
    );
  }

  return (
    <>
      {/* Map container — fills parent content area (flex-1 from AppShell layout) */}
      <div
        className="ph-no-capture h-full w-full relative overflow-hidden"
        data-testid="map-fullscreen-container"
      >
        {selectedProperty && (
          <PropertyMap
            property={selectedProperty}
            locations={waypoints}
            onMapReady={(map) => { mapRef.current = map; }}
            defaultTileLayer="satellite"
            hiddenOverlays={['boundary']}
          />
        )}

        {/* Wind info badge — top-3 right-3 */}
        {selectedProperty && (
          <WindInfoBadge propertyId={selectedProperty.id} />
        )}

        {/* Property pill — top-3 left-3 */}
        <div className="absolute top-3 left-3 z-[1001]">
          {selectedProperty && (
            <PropertyPill
              selectedProperty={selectedProperty}
              properties={properties}
              onSelect={setSelectedPropertyId}
            />
          )}
        </div>

        {/* GPS recenter button — stacked above FAB with 8px gap */}
        <button
          type="button"
          onClick={handleGps}
          disabled={isGpsLocating}
          aria-label="Go to my location"
          className="absolute bottom-[152px] right-3 z-[1001] flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text-primary)] hover:bg-[var(--color-elevated)] transition-colors shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
        >
          {isGpsLocating
            ? <Loader2 size={18} className="animate-spin" />
            : <Navigation size={18} />}
        </button>

        {/* No-locations overlay — shown when property exists but has no waypoints */}
        {effectivePropertyId && waypoints.length === 0 && (
          <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
            <div className="pointer-events-auto">
              <OperationalGuide
                illustration={noMapLocationsPng}
                heading="Pin Your First Waypoint"
                subtext="Mark stands, plots, and trails to map your land."
                ctaLabel="Add Waypoint"
                onCtaClick={() => { setTypePickerOpen(true); }}
                data-guide-id="map-no-locations"
              />
            </div>
          </div>
        )}

        {/* Add Location FAB — only rendered when there are waypoints (empty-state CTA is the primary action) */}
        {effectivePropertyId && waypoints.length > 0 && (
          <button
            type="button"
            data-testid="add-location-fab"
            aria-label="Add location"
            onClick={() => { setTypePickerOpen(true); }}
            className="absolute bottom-24 right-3 z-40 flex items-center justify-center w-12 h-12 rounded-lg bg-ember-500 shadow-ember focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember-400"
          >
            <Plus size={24} className="text-white" />
          </button>
        )}

      </div>

      {/* Type picker sheet — opens when FAB is tapped */}
      <TypePickerSheet
        isOpen={typePickerOpen}
        onTypeSelected={handleTypeSelected}
        onClose={() => setTypePickerOpen(false)}
      />
    </>
  );
}
